import * as React from "react";
import * as AlertDialogPrimitive from "@radix-ui/react-alert-dialog";

import { cn } from "../../lib/utils";
import { buttonVariants } from "./button";

const ConstrainedAlertDialog: React.FC<
  React.ComponentProps<typeof AlertDialogPrimitive.Root>
> = (props) => {
  // AlertDialog doesn't support modal prop like Dialog does
  return <AlertDialogPrimitive.Root {...props} />;
};

const ConstrainedAlertDialogTrigger = AlertDialogPrimitive.Trigger;

// Portal constrained to the dashboard content container
// Use layout effect to ensure the container is resolved after mount,
// which avoids falling back to <body> when triggered early (e.g., from sidebars)
const ConstrainedAlertDialogPortal = React.forwardRef<
  never,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Portal>
>(({ children, ...props }, _ref) => {
  const [container, setContainer] = React.useState<HTMLElement | null>(null);

  React.useLayoutEffect(() => {
    if (typeof document === "undefined") return;
    const el = document.querySelector(
      "#dashboard-main-content"
    ) as HTMLElement | null;
    if (el && el !== container) {
      setContainer(el);
    }
  }, [container]);

  return (
    <AlertDialogPrimitive.Portal container={container || undefined} {...props}>
      {children}
    </AlertDialogPrimitive.Portal>
  );
});
ConstrainedAlertDialogPortal.displayName = "ConstrainedAlertDialogPortal";

// Note: We use a custom in-portal overlay instead of Radix's Overlay so that
// sidebars can remain interactive while the middle area is dimmed/blocked.

const ConstrainedAlertDialogContent = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Content>
>(({ className, children, ...props }, ref) => {
  // Utility: resolve if an event occurred inside a sidebar (left or chat)
  const isInSidebar = React.useCallback((originalEvent?: Event) => {
    const getPath = () =>
      typeof originalEvent?.composedPath === "function"
        ? (originalEvent.composedPath() as Array<EventTarget>)
        : undefined;

    const matchesSidebar = (el: any) => {
      if (!el || !(el instanceof Element)) return false;
      if (el.closest('[data-sidebar="sidebar"]')) return true;
      if (el.closest(".resizable-chat-sidebar")) return true;
      return false;
    };

    const path = getPath();
    if (path && path.length) {
      for (const el of path) {
        if (matchesSidebar(el)) return true;
      }
      return false;
    }

    let target = originalEvent?.target as Element | null | undefined;
    if (target && target.nodeType === Node.TEXT_NODE) {
      target = (target as any).parentElement as Element | null;
    }
    return !!target && matchesSidebar(target);
  }, []);

  // AlertDialog doesn't support the same interaction handlers as Dialog
  // We'll handle closing through the overlay click instead

  // Handle overlay clicks to close the modal when clicking in the middle area
  const handleOverlayClick = (e: React.MouseEvent) => {
    // Only close if clicking the overlay itself, not its children
    // For AlertDialog, we can't programmatically close it like Dialog
    // The user will need to use the Cancel button or ESC key
    if (e.target === e.currentTarget && !isInSidebar(e.nativeEvent)) {
      // We could potentially trigger a click on the cancel button here
      // but it's better to let the user explicitly cancel
    }
  };

  // Prevent stacking with ownership semantics and guard against stale flags
  const ownerId = React.useMemo(() => {
    // Prefer crypto.randomUUID when available
    const rnd =
      (typeof crypto !== "undefined" && (crypto as any).randomUUID?.()) ||
      `owner-${Math.random().toString(36).slice(2)}`;
    return String(rnd);
  }, []);

  const [blockedByAnother, setBlockedByAnother] = React.useState(false);

  React.useLayoutEffect(() => {
    const body: any = document.body as any;
    const existingOwnerId: string | undefined = body.__constrainedModalOwnerId;

    // Helper to check if an element for an owner actually exists (prevents stale lockouts)
    const ownerElementExists = (id?: string) => {
      if (!id) return false;
      return !!document.querySelector(
        `[data-constrained-alert-dialog-owner="${CSS.escape(id)}"]`
      );
    };

    if (!existingOwnerId) {
      // No current owner → take ownership
      body.__constrainedModalOpen = true;
      body.__constrainedModalOwnerId = ownerId;
      setBlockedByAnother(false);
    } else if (existingOwnerId === ownerId) {
      // We already own it
      setBlockedByAnother(false);
    } else if (!ownerElementExists(existingOwnerId)) {
      // Stale lock → reclaim ownership
      body.__constrainedModalOpen = true;
      body.__constrainedModalOwnerId = ownerId;
      setBlockedByAnother(false);
    } else {
      // Another live modal is open → block this one
      setBlockedByAnother(true);
    }

    return () => {
      // Release ownership only if we are the owner
      if (body.__constrainedModalOwnerId === ownerId) {
        delete body.__constrainedModalOpen;
        delete body.__constrainedModalOwnerId;
      }
    };
  }, [ownerId]);

  if (blockedByAnother) {
    return null;
  }

  return (
    <ConstrainedAlertDialogPortal>
      {/* Manual overlay that blocks middle area but allows sidebar interaction */}
      <div
        className="absolute inset-0 z-[90] bg-black/80 cursor-pointer"
        onClick={handleOverlayClick}
        style={{
          pointerEvents: "auto",
        }}
      />
      <div className="absolute inset-0 z-[95] flex items-center justify-center pointer-events-none">
        <AlertDialogPrimitive.Content
          ref={ref}
          className={cn(
            "relative z-50 grid w-full max-w-lg gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg overflow-hidden",
            className
          )}
          style={{ pointerEvents: "auto" }} // Re-enable pointer events for the modal content
          data-constrained-alert-dialog-owner={ownerId}
          {...props}
        >
          {children}
        </AlertDialogPrimitive.Content>
      </div>
    </ConstrainedAlertDialogPortal>
  );
});
ConstrainedAlertDialogContent.displayName = "ConstrainedAlertDialogContent";

const ConstrainedAlertDialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-2 text-center sm:text-left",
      className
    )}
    {...props}
  />
);
ConstrainedAlertDialogHeader.displayName = "ConstrainedAlertDialogHeader";

const ConstrainedAlertDialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className
    )}
    {...props}
  />
);
ConstrainedAlertDialogFooter.displayName = "ConstrainedAlertDialogFooter";

const ConstrainedAlertDialogTitle = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold", className)}
    {...props}
  />
));
ConstrainedAlertDialogTitle.displayName = "ConstrainedAlertDialogTitle";

const ConstrainedAlertDialogDescription = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
));
ConstrainedAlertDialogDescription.displayName =
  "ConstrainedAlertDialogDescription";

const ConstrainedAlertDialogAction = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Action>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Action>
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Action
    ref={ref}
    className={cn(buttonVariants(), className)}
    {...props}
  />
));
ConstrainedAlertDialogAction.displayName = "ConstrainedAlertDialogAction";

const ConstrainedAlertDialogCancel = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Cancel>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Cancel>
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Cancel
    ref={ref}
    className={cn(
      buttonVariants({ variant: "outline" }),
      "mt-2 sm:mt-0",
      className
    )}
    {...props}
  />
));
ConstrainedAlertDialogCancel.displayName = "ConstrainedAlertDialogCancel";

export {
  ConstrainedAlertDialog,
  ConstrainedAlertDialogPortal,
  ConstrainedAlertDialogTrigger,
  ConstrainedAlertDialogContent,
  ConstrainedAlertDialogHeader,
  ConstrainedAlertDialogFooter,
  ConstrainedAlertDialogTitle,
  ConstrainedAlertDialogDescription,
  ConstrainedAlertDialogAction,
  ConstrainedAlertDialogCancel,
};
